import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { NCBPReportsResponseSummary } from "../../../../../api/services/reports/models";
import { ReactComponent as ExportIcon } from "../../../../../assets/images/export.svg";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonTertiary,
  MuiDateFieldFormController,
  MuiLink,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  TableHeaderSearchIcon,
} from "../../../../../components";
import {
  MAX_LENGTH_50,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import {
  filterListByFilterKeyValue,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { mapNCBPReports } from "../../../helper";
import {
  NCBPReportsListColumnsKey,
  NCBPReportsSummaryRow,
} from "../../../modelsAndSchemas/models";
import { reportsResultsSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

const pageId = `${PAGE_IDS.REPORTS}-list`;

const {
  dossierNumber,
  keyNumber,
  type,
  client,
  clientType,
  programProjectName,
  actualClient,
  invoiceNumber,
  invoiceDate,
  invoiceAmount,
  invoiceLineNumber,
  invoiceLineItemDetails,
  invoiceLineTotalBillingAmount,
} = NCBPReportsListColumnsKey;

interface NCBPReportsListProps {
  ncbpReports: NCBPReportsResponseSummary[];
  onExport: () => void;
}
export const NCBPReportsList = ({ ncbpReports, onExport }: NCBPReportsListProps) => {
  const {
    commonLabels,
    useFetchLabel,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [state, setState] = useState<{
    filteredReports: NCBPReportsSummaryRow[];
  }>({
    filteredReports: [],
  });

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const getValueByKeyOfDropdown = (listKey: string, key: string) =>
    fetchValueByKeyOfDropdown(reportsResultsSiteCoreKeys.header, listKey, key);

  const formattedNCBPReports = useMemo(() => {
    if (!ncbpReports?.length) return [];
    return mapNCBPReports(
      ncbpReports,
      getValueOrBlank(userCountryDateFormat),
      getValueByKeyOfDropdown,
    );
  }, [ncbpReports, userCountryDateFormat]);

  const { control, getValues } = useForm({
    defaultValues: {
      [dossierNumber]: "",
      [keyNumber]: "",
      [type]: "",
      [client]: "",
      [clientType]: "",
      [programProjectName]: "",
      [actualClient]: "",
      [invoiceNumber]: "",
      [invoiceDate]: "",
      [invoiceAmount]: "",
      [invoiceLineNumber]: "",
      [invoiceLineItemDetails]: "",
      [invoiceLineTotalBillingAmount]: "",
    },
  });

  //   watch entire form state
  const filters = useWatch({ control });

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    appliedFilters();
  }, [filters]);

  const appliedFilters = () => {
    if (!formattedNCBPReports.length) return;

    const filterList = filterListByFilterKeyValue(formattedNCBPReports, {
      ...filters,
      invoiceDate: filters.invoiceDate,
    }) as NCBPReportsSummaryRow[];

    setState((prevState) => ({
      ...prevState,
      filteredReports: filterList,
    }));
    resetPagination();
  };

  useEffect(() => {
    if (formattedNCBPReports) {
      setState((prevState) => ({
        ...prevState,
        filteredReports: formattedNCBPReports,
      }));
    }
  }, [formattedNCBPReports]);

  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const getLabel = useFetchLabel(reportsResultsSiteCoreKeys.header);

  const getHrefForNavigation = (
    programProjectId: string,
    isRecordTypeProgram: boolean,
  ) => {
    if (isRecordTypeProgram) {
      return `${PAGE_URLS.VIEW_PROGRAM}/${programProjectId}`;
    } else {
      return `${PAGE_URLS.VIEW_PROJECT}/${programProjectId}`;
    }
  };

  const columns: Column[] = [
    {
      id: dossierNumber,
      label: getLabel(reportsResultsSiteCoreKeys.dossierNumberTableColumn),
      isSortVisible: true,
    },
    {
      id: keyNumber,
      label: getLabel(reportsResultsSiteCoreKeys.programKeyTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiLink
          href={getHrefForNavigation(row["id"], row["isRecordTypeProgram"])}
          isOpenNewTab
        >
          <MuiBox>
            <MuiTypography variant="h3" className="Link">
              {row[keyNumber]}
            </MuiTypography>
          </MuiBox>
        </MuiLink>
      ),
    },
    {
      id: type,
      label: getLabel(reportsResultsSiteCoreKeys.type),
      minWidth: 200,
      isSortVisible: true,
    },
    {
      id: client,
      label: getLabel(reportsResultsSiteCoreKeys.clientName),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiLink
          href={`${PAGE_URLS.VIEW_CLIENT}/${row["clientId"]}`}
          isOpenNewTab
        >
          <MuiBox>
            <MuiTypography variant="h3" className="Link">
              {row[client]}
            </MuiTypography>
          </MuiBox>
        </MuiLink>
      ),
    },
    {
      id: clientType,
      label: getLabel(reportsResultsSiteCoreKeys.clientTypeTableColumn),
      minWidth: 200,
      isSortVisible: true,
    },
    {
      id: programProjectName,
      label: getLabel(reportsResultsSiteCoreKeys.participantName),
      isSortVisible: true,
    },
    {
      id: actualClient,
      label: getLabel(reportsResultsSiteCoreKeys.actualClientTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiLink
          href={`${PAGE_URLS.VIEW_CLIENT}/${row["actualClientId"]}`}
          isOpenNewTab
        >
          <MuiBox>
            <MuiTypography variant="h3" className="Link">
              {row[actualClient]}
            </MuiTypography>
          </MuiBox>
        </MuiLink>
      ),
    },
    {
      id: invoiceNumber,
      label: getLabel(reportsResultsSiteCoreKeys.invoiceNumberTableColumn),
      isSortVisible: true,
    },
    {
      id: invoiceDate,
      label: getLabel(reportsResultsSiteCoreKeys.invoiceDateTableColumn),
      isSortVisible: true,
    },
    {
      id: invoiceAmount,
      label: getLabel(reportsResultsSiteCoreKeys.invoiceAmountTableColumn),
      isSortVisible: true,
    },
    {
      id: invoiceLineNumber,
      label: getLabel(reportsResultsSiteCoreKeys.invoiceLineNumberTableColumn),
      isSortVisible: true,
    },
    {
      id: invoiceLineItemDetails,
      label: getLabel(
        reportsResultsSiteCoreKeys.invoiceLineItemDetailsTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: invoiceLineTotalBillingAmount,
      label: getLabel(
        reportsResultsSiteCoreKeys.invoiceLineTotalBillingAmountTableColumn,
      ),
      isSortVisible: true,
    },
  ];

  const renderFilterTextField = (name: string) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextField
          key={name}
          id={`${pageId}-${name}`}
          aria-label={commonLabels.search}
          startAdornment={<TableHeaderSearchIcon />}
          showCloseIcon={true}
          placeholder={commonLabels.search}
          maxLength={MAX_LENGTH_50}
          requiredARIALabel={commonARIALabel.required}
          shouldTrimSpaces={false}
        />
      </FormController>
    );
  };

  const renderFilterDropdown = (name: string, listKey: string) => {
    const options = fetchDropdownValues(
      reportsResultsSiteCoreKeys.header,
      listKey,
      true,
    );
    return (
      <FormController key={name} control={control} name={name}>
        <MuiSelect
          id={`${pageId}-${name}`}
          value=""
          label=""
          options={options}
          visibleDefaultSelectOption={true}
          requiredARIALabel={commonARIALabel.required}
          placeholder={commonLabels.selectPlaceHolder}
        />
      </FormController>
    );
  };

  const tableHeaderForm: React.JSX.Element[] = [
    renderFilterTextField(dossierNumber),
    renderFilterTextField(keyNumber),
    renderFilterDropdown(type, reportsResultsSiteCoreKeys.typeList),
    renderFilterTextField(client),
    renderFilterDropdown(clientType, reportsResultsSiteCoreKeys.clientTypeList),
    renderFilterTextField(programProjectName),
    renderFilterTextField(actualClient),
    renderFilterTextField(invoiceNumber),
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${invoiceDate}`}
      id={`${pageId}-${invoiceDate}`}
      name={invoiceDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(getValues(invoiceDate))}
    />,
    renderFilterTextField(invoiceAmount),
    renderFilterTextField(invoiceLineNumber),
    renderFilterDropdown(
      invoiceLineItemDetails,
      reportsResultsSiteCoreKeys.billingItemTypeList,
    ),
    renderFilterTextField(invoiceLineTotalBillingAmount),
  ];

  return (
    <MuiBox className="result-container">
      <MuiBox className="result-header">
        <MuiTypography variant="h2SemiBold" className="header-text">
          {commonLabels.allRecords}
        </MuiTypography>
        {ncbpReports.length > 0 && (
          <MuiTypography variant="h3SemiBold" className="header-count">
            {ncbpReports.length}
          </MuiTypography>
        )}
        <MuiBox textAlign="right" flexGrow={1}>
          <MuiButtonTertiary onClick={onExport}>
            <ExportIcon />
            {commonLabels.export}
          </MuiButtonTertiary>
        </MuiBox>
      </MuiBox>

      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        noResultMessage={commonLabels.noRecordsFound}
        tableResults={
          state.filteredReports as unknown as Record<string, ReactNode>[]
        }
        noDataInApi={ncbpReports.length === 0}
        emptyMessage={commonLabels.searchCriteria}
        priorityResults={[]}
        isShowDashIfValueEmpty
      />
    </MuiBox>
  );
};
